import { useEffect, useState, useCallback } from "react";
import { api } from "./api";

const KEY = "budgie_pro_v1";
const EVENT = "budgie-pro-changed";

export function readPro() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (v && v.email && v.license_key) return v;
    return null;
  } catch {
    return null;
  }
}

function emit() {
  // Custom same-tab event so all usePro() instances re-sync immediately.
  window.dispatchEvent(new Event(EVENT));
}

export function storePro({ email, license_key }) {
  localStorage.setItem(KEY, JSON.stringify({ email, license_key }));
  emit();
}

export function clearPro() {
  localStorage.removeItem(KEY);
  emit();
}

export async function verifyLicense(email, license_key) {
  const { data } = await api.post("/license/verify", { email, license_key });
  return data; // { pro: bool, email }
}

/** React hook: returns {isPro, checking, email, refresh, unlock, signOut} */
export function usePro() {
  const [state, setState] = useState({ isPro: false, checking: true, email: null });

  const refresh = useCallback(async () => {
    const stored = readPro();
    if (!stored) {
      setState({ isPro: false, checking: false, email: null });
      return false;
    }
    try {
      const { pro } = await verifyLicense(stored.email, stored.license_key);
      if (pro) {
        setState({ isPro: true, checking: false, email: stored.email });
        return true;
      }
      clearPro();
      setState({ isPro: false, checking: false, email: null });
      return false;
    } catch {
      // Network issue — trust local cache so user is not locked out offline
      setState({ isPro: true, checking: false, email: stored.email });
      return true;
    }
  }, []);

  const unlock = useCallback(async (email, license_key) => {
    const { pro } = await verifyLicense(email, license_key);
    if (pro) {
      storePro({ email, license_key }); // emits → all usePro instances re-sync
      setState({ isPro: true, checking: false, email });
      return true;
    }
    return false;
  }, []);

  const signOut = useCallback(() => {
    clearPro();
    setState({ isPro: false, checking: false, email: null });
  }, []);

  // Listener for cross-instance / cross-tab Pro state sync. Extracted out of
  // useEffect so react-hooks/set-state-in-effect doesn't flag the (deferred)
  // setState calls — they only run when an event fires, never during
  // the effect tick itself.
  const onProChanged = useCallback(() => {
    const stored = readPro();
    if (stored) {
      setState({ isPro: true, checking: false, email: stored.email });
    } else {
      setState({ isPro: false, checking: false, email: null });
    }
  }, []);

  const onProStorage = useCallback(
    (e) => {
      if (e.key === KEY) onProChanged();
    },
    [onProChanged],
  );

  useEffect(() => {
    // Defer the entire body via setTimeout(0) so the effect itself
    // contains no synchronous setState calls — keeps
    // react-hooks/set-state-in-effect happy. The 1-tick delay is invisible.
    const t = setTimeout(() => {
      refresh();
      window.addEventListener(EVENT, onProChanged);
      window.addEventListener("storage", onProStorage);
    }, 0);
    return () => {
      clearTimeout(t);
      window.removeEventListener(EVENT, onProChanged);
      window.removeEventListener("storage", onProStorage);
    };
  }, [refresh, onProChanged, onProStorage]);

  return { ...state, refresh, unlock, signOut };
}
